/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { push, replace } from "connected-react-router";
import { ActionsObservable, ofType, StateObservable } from "redux-observable";
import { of } from "rxjs";
import { filter, map, mapTo, mergeMap, switchMap } from "rxjs/operators";

import { ANONYMOUS_ID, ROUTES } from "../../constants";
import { AuthResult } from "../../enums";
import { DeviceHelper, PlatformHelper, StorageHelper } from "../../helpers";
import {
  IAuthResponseModel,
  IErrorModel,
  isFieldError,
  ITokenModel,
  IUserDeviceModel,
  IUserInfoModel,
  stringToErrorModel,
} from "../../models";
import { DataProvider } from "../../providers/DataProvider";
import { StorageKey, StorageManager } from "../../services";
import { Actions as MediaActions } from "../media";
import { IAppState } from "../types";
import * as UserActions from "../user/actions";

import * as Actions from "./actions";
import * as Consts from "./consts";
import * as Types from "./types";

export const signInEpic = (action$: ActionsObservable<Types.ISignInAction>) =>
  action$.pipe(
    ofType(Consts.SIGN_IN),
    switchMap(async (action: Types.ISignInAction) => {
      try {
        const device = await DeviceHelper.getDeviceInfo();
        const payload = { ...action.data };
        if (!payload.Device) {
          payload.Device = device;
        }

        const response = await DataProvider.signIn(payload);
        await StorageHelper.setUser(response.User);
        await StorageManager.setValue(
          StorageKey.session,
          response.AuthorizationToken as ITokenModel,
        );
        return Actions.signInSuccess(
          response.User,
          response.AuthorizationToken,
        );
      } catch (error) {
        return Actions.signInFailure(error as IErrorModel);
      }
    }),
  );

export const signInViaCodeEpic = (
  action$: ActionsObservable<Types.ISignInViaCodeSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_VIA_CODE_SUCCESS),
    switchMap(async (action: Types.ISignInViaCodeSuccessAction) => {
      try {
        const data = { ...action.payload };

        await StorageHelper.setUser(data.user);
        await StorageManager.setValue(
          StorageKey.session,
          data.session as ITokenModel,
        );
        return Actions.signInSuccess(data.user, data.session);
      } catch (error) {
        return Actions.signInFailure(error as IErrorModel);
      }
    }),
  );

export const signInSocialEpic = (
  action$: ActionsObservable<Types.ISignInSocialAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_SOCIAL),
    switchMap(async (action: Types.ISignInSocialAction) => {
      try {
        const device = await DeviceHelper.getDeviceInfo();
        const payload = { ...action.data };
        if (!payload.Device) {
          payload.Device = device;
        }

        const response = await DataProvider.signInSocial?.(payload);

        if (!response) {
          return Actions.signInSocialFailure({ Message: "Not implemented" });
        }

        const isSuccess = !!response.AuthorizationToken;

        if (isSuccess) {
          await StorageHelper.setUser(response.User);
          await StorageManager.setValue(
            StorageKey.session,
            response.AuthorizationToken as ITokenModel,
          );
          return Actions.signInSuccess(
            response.User,
            response.AuthorizationToken,
          );
        }

        return Actions.signInSocialRedirect(
          payload.Token,
          payload.Provider,
          response.User,
        );
      } catch (error) {
        return Actions.signInSocialFailure(error as IErrorModel);
      }
    }),
  );

const signInSuccessEpic = (
  action$: ActionsObservable<Types.ISignInSuccessAction>,
  state: StateObservable<IAppState>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_SUCCESS),
    map(() => {
      const from = state?.value.router?.location?.state?.from || ROUTES.BASE;
      return push(
        (from !== ROUTES.BASE && !PlatformHelper.isTizen) ||
          (from !== ROUTES.BASE && !PlatformHelper.isWebos)
          ? from
          : ROUTES.HOME,
      );
    }),
  );

const signInSocialRedirectEpic = (
  action$: ActionsObservable<Types.ISignInSocialRedirectAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_SOCIAL_REDIRECT),
    switchMap(async (action) => {
      return push({
        pathname: ROUTES.REGISTER_SOCIAL,
        state: action.payload,
      });
    }),
  );

export const signInAnonymousEpic = (
  action$: ActionsObservable<Types.ISignInAnonymousAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_ANONYMOUS),
    switchMap((action: Types.ISignInAnonymousAction) =>
      DeviceHelper.getDeviceInfo()
        .then((deviceInfo: IUserDeviceModel) => {
          const payload = { ...action.data };

          if (!payload.Device) {
            payload.Device = deviceInfo;
          }

          return DataProvider.signIn(payload);
        })
        .then((response: IAuthResponseModel) => {
          return Actions.signInAnonymousSuccess(
            response.User,
            response.AuthorizationToken,
          );
        })
        .catch((error: IErrorModel) => {
          return Actions.signInAnonymousFailure(error);
        }),
    ),
  );

const signInAnonymousSuccessEpic = (
  action$: ActionsObservable<Types.ISignInAnonymousSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_IN_ANONYMOUS_SUCCESS),
    switchMap(async (action: Types.ISignInAnonymousSuccessAction) => {
      try {
        await StorageHelper.setUser(action.payload.user);
        if (action.payload.session) {
          await StorageManager.setValue(
            StorageKey.session,
            action.payload.session as ITokenModel,
          );
        }
        return Actions.setAuthenticated();
      } catch (error) {
        if (__DEV__) {
          console.error("SIGN_IN_ANONYMOUS_ERROR: ", error);
        }
        return Actions.setAuthenticated();
      }
    }),
  );

const refreshUserEpic = (
  action$: ActionsObservable<Types.IRefreshUserAction>,
) =>
  action$.pipe(
    ofType(Consts.REFRESH_USER),
    switchMap((action: Types.IRefreshUserAction) => {
      return StorageHelper.getUser()
        .then((user: IUserInfoModel | null) => {
          if (user) {
            user.AvatarUrl = action.payload.AvatarUrl;
            user.FullName = action.payload.FullName;

            return StorageHelper.setUser(user).then(() => {
              return { type: "NEVER" };
            });
          }
          return { type: "NEVER" };
        })
        .catch(() => {
          return { type: "NEVER" };
        });
    }),
  );

const syncUserEpic = (action$: ActionsObservable<Types.ISyncUserAction>) =>
  action$.pipe(
    ofType(Consts.SYNC_USER),
    switchMap(async () => {
      try {
        const userInfo = await DataProvider.syncUser();

        if (!userInfo) {
          return Actions.syncUserFailure();
        }

        return Actions.syncUserSuccess(userInfo);
      } catch (error) {
        return Actions.syncUserFailure();
      }
    }),
  );

const syncUserSuccessEpic = (
  action$: ActionsObservable<Types.ISyncUserSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.SYNC_USER_SUCCESS),
    filter(({ payload }) => {
      return !!(payload.Id !== ANONYMOUS_ID && payload.Email);
    }),
    map(() => UserActions.getProducts()),
  );

const signOutEpic = (action$: ActionsObservable<Types.ISignOutAction>) =>
  action$.pipe(
    ofType(Consts.SIGN_OUT),
    switchMap(async () => {
      try {
        const device = await DeviceHelper.getDeviceInfo();
        await DataProvider.signOut(device);
        await StorageHelper.deleteUser();
        await StorageManager.deleteValue(StorageKey.session);
        return Actions.signOutSuccess();
      } catch (error) {
        return Actions.signOutFailure(error as IErrorModel);
      }
    }),
  );

const registerEmailEpic = (
  action$: ActionsObservable<Types.IRegisterEmailAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_EMAIL),
    switchMap((action: Types.IRegisterEmailAction) =>
      DataProvider.registerEmail(action.data)
        .then(() =>
          Actions.registerEmailSuccess(action.data.Email, action.redirectUrl),
        )
        .catch((error: IErrorModel) => Actions.registerEmailFailure(error)),
    ),
  );

const registerConfirmEmailEpic = (
  action$: ActionsObservable<Types.IRegisterConfirmEmailAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_CONFIRM_EMAIL),
    switchMap((action: Types.IRegisterConfirmEmailAction) =>
      DataProvider.registerConfirmEmail(action.data)
        .then((response) =>
          Actions.registerConfirmEmailSuccess(
            response.User,
            response.AuthorizationToken,
            action.redirectUrl,
          ),
        )
        .catch((error: string) =>
          Actions.registerConfirmEmailFailure(stringToErrorModel(error)),
        ),
    ),
  );

export const registerConfirmAccountWithPasswordEpic = (
  action$: ActionsObservable<Types.IConfirmAccountWithPasswordAction>,
) =>
  action$.pipe(
    ofType(Consts.CONFIRM_ACCOUNT_WITH_PASSWORD),
    switchMap((action: Types.IConfirmAccountWithPasswordAction) =>
      DataProvider.registerConfirmAccount(action.data)
        .then((response) =>
          Actions.registerConfirmEmailSuccess(
            response.User,
            response.AuthorizationToken,
            action.redirectUrl,
          ),
        )
        .catch((error: string) =>
          Actions.registerConfirmEmailFailure(stringToErrorModel(error)),
        ),
    ),
  );

export const refreshTokenEpic = (
  action$: ActionsObservable<Types.IRefreshTokenAction>,
  state: StateObservable<IAppState>,
) => {
  return action$.pipe(
    ofType(Consts.REFRESH_TOKEN),
    switchMap(() => {
      const token = state.value.auth.session?.RefreshToken as string;

      return DeviceHelper.getDeviceInfo()
        .then((deviceInfo: IUserDeviceModel) => {
          return DataProvider.refreshToken(token, deviceInfo);
        })
        .then((response: IAuthResponseModel) => {
          if (
            response.AuthorizationToken &&
            response.AuthorizationToken.AuthResult === AuthResult.OK
          ) {
            return Actions.refreshTokenSuccess(
              response.AuthorizationToken,
              response.User,
            );
          }
          return Actions.refreshTokenFailure();
        })
        .catch(() => {
          return Actions.refreshTokenFailure();
        });
    }),
  );
};

const refreshTokenSuccessEpic = (
  action$: ActionsObservable<Types.IRefreshTokenSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.REFRESH_TOKEN_SUCCESS),
    switchMap((action: Types.IRefreshTokenSuccessAction) => {
      return StorageHelper.setUser(action.payload.user).then(() => {
        return StorageManager.setValue(
          StorageKey.session,
          action.payload.session,
        ).then(() => {
          return { type: "NEVER" };
        });
      });
    }),
  );

const refreshTokenFailureEpic = (
  action$: ActionsObservable<Types.IRefreshTokenFailureAction>,
) =>
  action$.pipe(
    ofType(Consts.REFRESH_TOKEN_FAILURE),
    map((_action: Types.IRefreshTokenFailureAction) => {
      return StorageHelper.deleteUser().then(() => {
        return StorageManager.deleteValue(StorageKey.session);
      });
    }),
    mapTo(push(ROUTES.BASE)),
  );

export const changePasswordEpic = (
  action$: ActionsObservable<Types.IChangePasswordAction>,
) =>
  action$.pipe(
    ofType(Consts.CHANGE_PASSWORD),
    switchMap((action: Types.IChangePasswordAction) =>
      DataProvider.changePassword(action.payload)
        .then((data) => Actions.changePasswordSuccess(data))
        .catch((error: IErrorModel) => Actions.changePasswordFailure(error)),
    ),
  );

const forgotPasswordFailureEpic = (
  action$: ActionsObservable<Types.IForgotPasswordFailureAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD_LINK_FAILURE),
    mapTo(push(ROUTES.FORGOT_PASSWORD_SUCCESS)),
  );

const registerConfirmEmailSuccessEpic = (
  action$: ActionsObservable<Types.IRegisterConfirmEmailSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_CONFIRM_EMAIL_SUCCESS),
    switchMap(async (action: Types.IRegisterConfirmEmailSuccessAction) => {
      const redirectUrl = action.payload.redirectUrl || ROUTES.HOME;
      const user = action.payload.user;
      const session = action.payload.session;

      try {
        if (session) {
          await StorageManager.setValue(StorageKey.session, session);
        }

        if (user) {
          await StorageHelper.setUser(user);

          const products = await DataProvider.getProducts();

          if (products) {
            user.Products = products;
            await StorageHelper.setUser(user);
          }
        }

        return push(redirectUrl);
      } catch (error) {
        console.error(error);
        return push(redirectUrl);
      }
    }),
  );

const registerSocialEpic = (
  action$: ActionsObservable<Types.IRegisterSocialAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_SOCIAL),
    switchMap(async (action: Types.IRegisterSocialAction) => {
      try {
        const device = await DeviceHelper.getDeviceInfo();
        const payload = { ...action.data };

        if (!payload.Device) {
          payload.Device = device;
        }

        const response = await DataProvider.registerSocial?.(payload);

        if (!response) {
          return Actions.registerSocialFailure({ Message: "Not implemented" });
        }

        return Actions.registerSocialSuccess(
          response.User,
          response.AuthorizationToken,
          action.redirectUrl,
        );
      } catch (error) {
        return Actions.registerSocialFailure(error as IErrorModel);
      }
    }),
  );

const registerSocialSuccessEpic = (
  action$: ActionsObservable<Types.IRegisterSocialSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_SOCIAL_SUCCESS),
    switchMap(async (action: Types.IRegisterSocialSuccessAction) => {
      const redirectUrl = action.payload.redirectUrl || ROUTES.HOME;
      const user = action.payload.user;
      const session = action.payload.session;

      try {
        if (session) {
          await StorageManager.setValue(StorageKey.session, session);
        }

        if (user) {
          await StorageHelper.setUser(user);

          const products = await DataProvider.getProducts();

          if (products) {
            user.Products = products;
            await StorageHelper.setUser(user);
          }
        }

        return push(redirectUrl);
      } catch (error) {
        console.error(error);
        return push(redirectUrl);
      }
    }),
  );

export const validateTokenEpic = (
  action$: ActionsObservable<Types.IValidateTokenAction>,
) =>
  action$.pipe(
    ofType(Consts.VALIDATE_TOKEN),
    switchMap((action: Types.IValidateTokenAction) =>
      DataProvider.validateConfirmationToken(action.payload.token)
        .then(() => Actions.validateTokenSuccess())
        .catch((error: IErrorModel) => Actions.validateTokenFailure(error)),
    ),
  );

const forgotPasswordEpic = (
  action$: ActionsObservable<Types.IForgotPasswordAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD_LINK),
    switchMap((action: Types.IForgotPasswordAction) =>
      DataProvider.forgotPassword(action.payload)
        .then((response) => Actions.forgotPasswordSuccess(response))
        .catch((error: IErrorModel) => Actions.forgotPasswordFailure(error)),
    ),
  );

const resetForgotPasswordEpic = (
  action$: ActionsObservable<Types.IResetForgotPasswordAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD),
    switchMap((action: Types.IResetForgotPasswordAction) =>
      DataProvider.resetForgotPassword(action.payload)
        .then((response) => Actions.resetForgotPasswordSuccess(response))
        .catch((error: IErrorModel) =>
          Actions.resetForgotPasswordFailure(error),
        ),
    ),
  );

export const registerEmailSuccessRedirectEpic = (
  action$: ActionsObservable<Types.IRegisterEmailSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.REGISTER_EMAIL_SUCCESS),
    filter(
      (action: Types.IRegisterEmailSuccessAction) =>
        !!action.payload.redirectUrl,
    ),
    map((action: Types.IRegisterEmailSuccessAction) => {
      return push({
        pathname: action.payload.redirectUrl || ROUTES.BASE,
        state: { email: action.payload.email },
      });
    }),
  );

export const resetPasswordLinkSuccessEpic = (
  action$: ActionsObservable<Types.IResetPasswordLinkSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD_LINK_SUCCESS),
    mapTo(replace("/forgot-password-success")),
  );

export const resetPasswordLinkFailureEpic = (
  action$: ActionsObservable<Types.IResetPasswordLinkFailureAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD_LINK_FAILURE),
    filter((action: Types.IResetPasswordLinkFailureAction) =>
      action.error ? !isFieldError(action.error) : true,
    ),
    mapTo(replace("/forgot-password-success")),
  );

export const resetPasswordSuccessEpic = (
  action$: ActionsObservable<Types.IResetPasswordSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.RESET_PASSWORD_SUCCESS),
    mapTo(push(ROUTES.LOGIN)),
  );

export const resendConfirmationEmailByUserEpic = (
  action$: ActionsObservable<Types.IResendConfirmationEmailByUserAction>,
) =>
  action$.pipe(
    ofType(Consts.RESEND_CONFIRMATION_EMAIL_BY_USER),
    switchMap((action: Types.IResendConfirmationEmailByUserAction) =>
      DataProvider.resendConfirmationEmailByUser(action.data)
        .then(() => Actions.resendConfirmationEmailByUserSuccess())
        .catch(() => Actions.resendConfirmationEmailByUserFailure()),
    ),
  );

const signOutSuccessRedirectEpic = (
  action$: ActionsObservable<Types.ISignOutSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_OUT_SUCCESS),
    mergeMap(() => {
      return of(Actions.signInAnonymous(), replace(ROUTES.LOGIN));
    }),
  );

const signOutSuccessClearMediaEpic = (
  action$: ActionsObservable<Types.ISignOutSuccessAction>,
) =>
  action$.pipe(
    ofType(Consts.SIGN_OUT_SUCCESS),
    switchMap(async () => {
      await StorageManager.deleteValue(StorageKey.source);
      return MediaActions.clearMedia();
    }),
  );

export const authEpics = [
  syncUserEpic,
  signInEpic,
  signInSuccessEpic,
  signInAnonymousEpic,
  signInAnonymousSuccessEpic,
  signOutEpic,
  signOutSuccessRedirectEpic,
  registerEmailEpic,
  registerEmailSuccessRedirectEpic,
  registerConfirmEmailEpic,
  registerConfirmAccountWithPasswordEpic,
  registerConfirmEmailSuccessEpic,
  refreshTokenEpic,
  refreshTokenSuccessEpic,
  refreshTokenFailureEpic,
  changePasswordEpic,
  refreshUserEpic,
  validateTokenEpic,
  resetForgotPasswordEpic,
  resetPasswordLinkSuccessEpic,
  resetPasswordLinkFailureEpic,
  resetPasswordSuccessEpic,
  forgotPasswordEpic,
  forgotPasswordFailureEpic,
  resendConfirmationEmailByUserEpic,
  signOutSuccessClearMediaEpic,
  syncUserSuccessEpic,
  signInViaCodeEpic,
  signInSocialEpic,
  signInSocialRedirectEpic,
  registerSocialEpic,
  registerSocialSuccessEpic,
];
